import { useRequest } from 'features/request/useRequest';
import type { WorkOrderPriceItem } from 'gql/graphql';
import { WorkOrderPriceTypeEnum } from 'gql/graphql';

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { uerRemoveWorkOrderPriceItemsGql } from '../../workOrderQueries';

export const useDeleteFromWorkOrder = ({
    workOrderId,
    checkedRows,
    singleDeleteRow,
}: {
    workOrderId: number;
    checkedRows?: Record<string, WorkOrderPriceItem>;
    singleDeleteRow?: WorkOrderPriceItem;
}) => {
    const dispatch = useDispatch();

    const [data, status, err, request, setData] = useRequest({
        requestId: 'deleteFromWorkOrder',
        query: uerRemoveWorkOrderPriceItemsGql,
        withEffect: false,
    });

    const variables = useMemo(() => {
        const singleDeleteIds =
            singleDeleteRow?.type === WorkOrderPriceTypeEnum.Other
                ? {
                      workOrderPriceIds: [],
                      workOrderOtherPriceIds: [singleDeleteRow?.itemId],
                  }
                : {
                      workOrderPriceIds: [singleDeleteRow?.itemId],
                      workOrderOtherPriceIds: [],
                  };

        const { workOrderPriceIds, workOrderOtherPriceIds } = Object.values(checkedRows ?? {})
            .filter(Boolean)
            .reduce(
                (acc: { workOrderPriceIds: number[]; workOrderOtherPriceIds: number[] }, item) => {
                    if (item?.type === WorkOrderPriceTypeEnum.Other) {
                        acc.workOrderOtherPriceIds.push(item?.itemId);
                    }

                    if (item?.type !== WorkOrderPriceTypeEnum.Other) {
                        acc.workOrderPriceIds.push(item?.itemId);
                    }

                    return acc;
                },
                {
                    workOrderPriceIds: [],
                    workOrderOtherPriceIds: [],
                },
            );

        if (singleDeleteRow) {
            return {
                ...singleDeleteIds,
                workOrderId,
            };
        }

        return {
            workOrderId,
            workOrderPriceIds,
            workOrderOtherPriceIds,
        };
    }, [checkedRows, singleDeleteRow, workOrderId]);

    const handleDeleteFromWorkOrder = useCallback(() => {
        dispatch(request(variables));
    }, [dispatch, request, variables]);

    return {
        deleteData: data?.uerRemoveWorkOrderPriceItems,
        deleteErr: err,
        deleteSetData: setData,
        deleteStatus: status,
        handleDeleteFromWorkOrder,
    };
};
